import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Truck } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { API_URL } from '@/lib/api';

const Settings = () => {
  const { admin, updateProfile, changePassword } = useAuth();
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [deliveryFetching, setDeliveryFetching] = useState(true);

  const [profileData, setProfileData] = useState({
    name: admin?.name || '',
    email: admin?.email || '',
  });

  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  const [deliverySettings, setDeliverySettings] = useState({
    deliveryCharge: 50,
    freeDeliveryAbove: 0,
  });

  useEffect(() => {
    const fetchDeliverySettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/settings`);
        setDeliverySettings({
          deliveryCharge: res.data.deliveryCharge ?? 50,
          freeDeliveryAbove: res.data.freeDeliveryAbove ?? 0,
        });
      } catch {
        // ignore, defaults are fine
      } finally {
        setDeliveryFetching(false);
      }
    };
    fetchDeliverySettings();
  }, []);

  const handleProfileChange = (e) => {
    setProfileData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePasswordChange = (e) => {
    setPasswordData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    try {
      await updateProfile(profileData);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleDeliverySubmit = async (e) => {
    e.preventDefault();
    setDeliveryLoading(true);
    try {
      await axios.patch(`${API_URL}/settings`, {
        deliveryCharge: Number(deliverySettings.deliveryCharge),
        freeDeliveryAbove: Number(deliverySettings.freeDeliveryAbove),
      });
      toast.success('Delivery settings updated');
    } catch {
      toast.error('Failed to update delivery settings');
    } finally {
      setDeliveryLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setPasswordLoading(true);
    try {
      await changePassword(passwordData.current_password, passwordData.new_password);
      toast.success('Password changed successfully');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div data-testid="settings-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="text-slate-500 mt-1 text-sm">Manage your account and preferences</p>
        </div>
      </div>

      <div className="max-w-2xl space-y-5">

        {/* ── Delivery Charge ─────────────────── */}
        <Card className="rounded-2xl border-slate-100 shadow-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Truck className="w-4 h-4 text-purple-600" /> Delivery Settings
            </CardTitle>
            <CardDescription className="text-sm">Configure delivery charges shown to store owners at checkout</CardDescription>
          </CardHeader>
          <CardContent>
            {deliveryFetching ? (
              <div className="flex items-center gap-2 text-slate-400 text-sm py-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading...
              </div>
            ) : (
              <form onSubmit={handleDeliverySubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="deliveryCharge" className="form-label">Delivery Charge (₹)</Label>
                    <Input
                      id="deliveryCharge"
                      type="number"
                      min="0"
                      step="1"
                      value={deliverySettings.deliveryCharge}
                      onChange={(e) => setDeliverySettings(prev => ({ ...prev, deliveryCharge: e.target.value }))}
                      className="rounded-xl border-slate-200 focus:border-purple-400 focus:ring-purple-400"
                    />
                  </div>
                  <div>
                    <Label htmlFor="freeDeliveryAbove" className="form-label">Free Delivery Above (₹)</Label>
                    <Input
                      id="freeDeliveryAbove"
                      type="number"
                      min="0"
                      step="1"
                      value={deliverySettings.freeDeliveryAbove}
                      onChange={(e) => setDeliverySettings(prev => ({ ...prev, freeDeliveryAbove: e.target.value }))}
                      className="rounded-xl border-slate-200 focus:border-purple-400 focus:ring-purple-400"
                    />
                    <p className="text-xs text-slate-400 mt-1">Set to 0 to disable free delivery threshold</p>
                  </div>
                </div>
                <Button type="submit" disabled={deliveryLoading} className="rounded-xl"
                  style={{ background: 'linear-gradient(135deg, #7c3aed, #6d28d9)' }}>
                  {deliveryLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : 'Save Delivery Settings'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        {/* ── Admin Profile ───────────────────── */}
        <Card className="rounded-2xl border-slate-100 shadow-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading">Admin Profile</CardTitle>
            <CardDescription className="text-sm">Update your account information</CardDescription>
          </CardHeader>
          <CardContent>
            {/* Avatar */}
            <div className="flex items-center gap-4 mb-5 p-3 bg-slate-50 rounded-xl">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}
              >
                {admin?.name?.charAt(0).toUpperCase() || 'A'}
              </div>
              <div>
                <p className="font-semibold text-slate-900 text-sm">{admin?.name}</p>
                <p className="text-xs text-slate-500">{admin?.email}</p>
              </div>
            </div>

            <form onSubmit={handleProfileSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name" className="form-label">Full Name</Label>
                <Input id="name" name="name" value={profileData.name}
                  onChange={handleProfileChange} placeholder="Your name" required
                  data-testid="profile-name-input"
                  className="rounded-xl border-slate-200 focus:border-purple-400 focus:ring-purple-400" />
              </div>
              <div>
                <Label htmlFor="email" className="form-label">Email Address</Label>
                <Input id="email" name="email" type="email" value={profileData.email}
                  onChange={handleProfileChange} placeholder="your@email.com" required
                  data-testid="profile-email-input"
                  className="rounded-xl border-slate-200 focus:border-purple-400 focus:ring-purple-400" />
              </div>
              <Button type="submit" disabled={profileLoading} data-testid="update-profile-btn"
                className="rounded-xl"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #6d28d9)' }}>
                {profileLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Updating…</> : 'Update Profile'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* ── Change Password ─────────────────── */}
        <Card className="rounded-2xl border-slate-100 shadow-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base font-heading">Change Password</CardTitle>
            <CardDescription className="text-sm">Update your account password</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <Label htmlFor="current_password" className="form-label">Current Password</Label>
                <Input id="current_password" name="current_password" type="password"
                  value={passwordData.current_password} onChange={handlePasswordChange}
                  placeholder="Enter current password" required
                  data-testid="current-password-input"
                  className="rounded-xl border-slate-200 focus:border-purple-400 focus:ring-purple-400" />
              </div>
              <Separator />
              <div>
                <Label htmlFor="new_password" className="form-label">New Password</Label>
                <Input id="new_password" name="new_password" type="password"
                  value={passwordData.new_password} onChange={handlePasswordChange}
                  placeholder="Minimum 6 characters" required
                  data-testid="new-password-input"
                  className="rounded-xl border-slate-200 focus:border-purple-400 focus:ring-purple-400" />
              </div>
              <div>
                <Label htmlFor="confirm_password" className="form-label">Confirm New Password</Label>
                <Input id="confirm_password" name="confirm_password" type="password"
                  value={passwordData.confirm_password} onChange={handlePasswordChange}
                  placeholder="Repeat new password" required
                  data-testid="confirm-password-input"
                  className="rounded-xl border-slate-200 focus:border-purple-400 focus:ring-purple-400" />
              </div>
              <Button type="submit" disabled={passwordLoading} data-testid="change-password-btn"
                className="rounded-xl"
                style={{ background: 'linear-gradient(135deg, #7c3aed, #6d28d9)' }}>
                {passwordLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Changing…</> : 'Change Password'}
              </Button>
            </form>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default Settings;
